import { useState, useEffect, useCallback, useMemo } from 'react'
import { ZendeskAPI } from 'react-zendesk'
import { useHistory } from 'react-router'
import { getCache, setCache } from '@/utils/cache'

import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'

const ZENDESK_KEY_CONEXA = '0bd69c8a-25a3-4d36-ad0f-715d64728898'
const ZENDESK_KEY_CLINIC = '871b18d3-d24f-4dd2-a41f-f3abef5f3c71'

type UseZendeskReturnType = {
  zendeskKey: string
  ZENDESK_SETTINGS: Record<string, unknown>
  openZendeskChat: () => void
  onZendeskLoaded: () => void
}

export const useZendesk = (): UseZendeskReturnType => {
  const [zendeskKey, setZendeskKey] = useState(() => {
    switch (getCache('zendesk-key')) {
      case 'conexa':
        return ZENDESK_KEY_CONEXA
      case 'clinic':
        return ZENDESK_KEY_CLINIC
      default:
        setCache('zendesk-key', 'conexa')
        return ZENDESK_KEY_CONEXA
    }
  })
  const history = useHistory()
  const { theme } = useThemeGeneratorContext()
  const { user, clinic } = useAuthContext()

  const ZENDESK_SETTINGS = useMemo(
    () => ({
      // PA-2809 -> algumas opções comentadas para funcionar como no script que o suporte enviou
      color: {
        theme: theme.colors.primary[500],
      },
      // position: {
      //   horizontal: 'right',
      //   vertical: 'bottom',
      // },
      zIndex: 99,
      // launcher: {
      //   label: {
      //     'pt-BR': 'Precisa de ajuda?',
      //   },
      //   chatLabel: {
      //     'pt-BR': 'Precisa de ajuda?',
      //   },
      // },
      contactForm: {
        // fields: [{ id: 'description', prefill: { '*': 'Preciso de ajuda' } }],
        suppress: true,
      },
      chat: {
        departments: {
          enabled: [
            'Paciente docpass',
            'Paciente',
            'Profissional de saúde',
            'iMedicina',
            'Sinch',
          ],
        },
        suppress: false,
        hideWhenOffline: true,
        connectOnPageLoad: false,
      },
      helpCenter: {
        suppress: true,
      },
      talk: {
        suppress: true,
      },
      answerBot: {
        suppress: true,
      },
      navigation: {
        popoutButton: {
          enabled: false,
        },
      },
    }),
    [theme.colors.primary],
  )

  const applyZendeskTags = useCallback(() => {
    if (user) {
      const tags = [
        `nome_${user.name}`,
        `login_${user.login}`,
        `cpf_${user.cpf}`,
        `nome_clinica_${user.clinic.name}`,
        `id_clinica_${user.clinic.id}`,
        'tipo_usuario_paciente',
        'local_Web',
      ]

      if (clinic?.hasZendeskConfig) {
        const specialCareTag = tags.find(tag => tag === 'cuidados_especiais')

        if (!specialCareTag) tags.push('cuidados_especiais')
      }

      ZendeskAPI('webWidget', 'chat:addTags', tags)
    }

    // Inicia o Zendesk com widget escondido
    ZendeskAPI('webWidget', 'hide')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (!clinic) {
      return null
    }

    // precisamos desse path para que o redirect do zendesk funcione com link mágico
    let redirectTo = history.location.pathname
      ?.replace(/_/g, '/')
      ?.split('/')
      ?.filter(i => i)
      ?.slice(2)
      ?.join('/')

    if (history.location.search) {
      redirectTo += history.location.search
    }

    if (clinic.hasZendeskConfig) {
      if (zendeskKey === ZENDESK_KEY_CONEXA) {
        setZendeskKey(ZENDESK_KEY_CLINIC)
        setCache('zendesk-key', 'clinic')

        // altera href pois não é possível alterar a chave do Zendesk
        // sem fazer o reload da página
        window.location.href = redirectTo ? `/${redirectTo}` : '/'
      }
    } else {
      if (zendeskKey === ZENDESK_KEY_CLINIC) {
        setZendeskKey(ZENDESK_KEY_CONEXA)
        setCache('zendesk-key', 'conexa')

        // altera href pois não é possível alterar a chave do Zendesk
        // sem fazer o reload da página
        window.location.href = redirectTo ? `/${redirectTo}` : '/'
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic, zendeskKey])

  const openZendeskChat = useCallback(() => {
    // exibe o widget do Zendezk
    ZendeskAPI('webWidget', 'show')

    // abre o widget do Zendek
    ZendeskAPI('webWidget', 'open')
  }, [])

  const onZendeskLoaded = useCallback(() => {
    applyZendeskTags()

    // PA-2809 -> comentado para funcionar como no script que o suporte enviou
    // ZendeskAPI('webWidget:on', 'close', () => {
    //   ZendeskAPI('webWidget', 'hide')
    // })
  }, [applyZendeskTags])

  return {
    zendeskKey,
    ZENDESK_SETTINGS,
    openZendeskChat,
    onZendeskLoaded,
  }
}
